import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable'; 


// ─── Helpers ────────────────────────────────────────────────────────────────

const getMovimentoConfig = (tipo, qta) => {
  const t = tipo?.toLowerCase() || '';
  const kolicina = Number(qta) || 0;
  
  const isScaricato = kolicina < 0 || t.includes('scaricato') || t.includes('-');
  const isCaricato = kolicina > 0 || t.includes('caricato') || t.includes('+');

  if (isScaricato) {
    return { 
      color: 'text-red-500', 
      dot: 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]', 
      badge: 'bg-red-500/10 border-red-500/30 text-red-500',
      bgHeader: 'from-red-500/15 to-transparent',
      symbol: '' 
    };
  }
  
  if (isCaricato) {
    return { 
      color: 'text-emerald-500', 
      dot: 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]', 
      badge: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500',
      bgHeader: 'from-emerald-500/15 to-transparent',
      symbol: '+'
    };
  }

  return { 
    color: 'text-slate-400', 
    dot: 'bg-slate-400', 
    badge: 'bg-slate-400/10 border-slate-400/30 text-slate-400',
    bgHeader: 'from-slate-500/5 to-transparent',
    symbol: ''
  };
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return '---';
  try {
    const d = new Date(dateStr);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Europe/Rome'
    }).format(d);
  } catch (e) {
    return dateStr;
  }
};

const toInputDateTime = (dateStr) => {
  if (!dateStr) return '';
  let d = new Date(dateStr);
  d.setTime(d.getTime() - (2 * 60 * 60 * 1000));
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// ─── Sotto-componenti ────────────────────────────────────────────────────────

const Campo = ({ label, value, mono = false, isEditing, onChange, type = "text", name, disabled = false }) => {
  const isFieldDisabled = disabled || (isEditing && name !== 'nota' && name !== 'data');

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">
        {label}
      </span>
      {isEditing ? (
        <input
          type={type}
          name={name}
          value={value ?? ''}
          onChange={onChange}
          disabled={isFieldDisabled}
          className={`bg-background border border-border/60 rounded-xl px-3 py-2.5 text-sm font-bold outline-none w-full transition-all shadow-sm ${
            isFieldDisabled ? 'opacity-50 cursor-not-allowed bg-muted/20' : 'focus:ring-2 focus:ring-primary/20 hover:border-primary/40'
          }`}
        />
      ) : (
        <span className={`text-[15px] font-bold text-foreground/90 break-words ${mono ? 'font-mono tracking-tight bg-muted/30 px-2 py-0.5 rounded' : ''}`}>
          {value ?? '---'}
        </span>
      )}
    </div>
  );
};

const Sezione = ({ titolo, children, icon, colorClass = "" }) => (
  <div className="flex flex-col gap-5 p-6 rounded-[2rem] bg-muted/20 border border-border/40 group hover:border-primary/20 transition-all duration-300">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-xl bg-background border border-border/40 ${colorClass}`}>
        {icon && <Icon name={icon} size={14} />}
      </div>
      <span className="text-[11px] font-black uppercase tracking-[.2em] text-foreground/60">
        {titolo}
      </span>
      <div className="flex-1 h-px bg-gradient-to-r from-border/40 to-transparent" />
    </div>
    <div className="grid gap-6">
      {children}
    </div>
  </div>
);

// ─── Componente principale ───────────────────────────────────────────────────

const DettaglioModalStorico = ({ item, onClose, onUpdate, fullData = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ...item });
  const [loading, setLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [notification, setNotification] = useState(null);

  if (!item) return null;

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // FUNKCIJA ZA PDF
const downloadPDF = () => {
  try {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const now = new Date().toLocaleString('it-IT');
    
    // Header
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204); 
    doc.text('OrizonDesk', 14, 20);
    
    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text('Storico Movimenti CCO', 14, 28);

    // Tabela - Mapiranje podataka
    const tableData = (fullData && fullData.length > 0 ? fullData : [item]).map(row => [
      formatFullDate(row.data),
      row.ean || '',
      row.nome || '',
      row.qtapresente,
      row.qtamovimentata,
      row.tipomovimento,
      row.nota || row.note || '' // Dodata nota (proverava oba naziva polja)
    ]);

    // Korišćenje autoTable
    autoTable(doc, {
      startY: 35,
      // DODAT 'Nota' U HEAD NIZ NA KRAJU
      head: [['Data', 'EAN', 'Articolo', 'Q.tà Pres.', 'Q.tà Mov.', 'Tipo Movimento', 'Nota']], 
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [0, 102, 204], fontSize: 10 },
      styles: { 
        fontSize: 9, 
        cellPadding: 3,
        overflow: 'linebreak' // Omogućava da se dugi tekst u noti prelomi u novi red
      },
      columnStyles: {
        6: { cellWidth: 'auto' } // Nota će zauzeti preostali prostor
      },
      margin: { left: 14, right: 14 }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Stampato il: ${now}`, 14, 200);
      doc.text('https://desk.orizon-aix.com', 283, 200, { align: 'right' });
    }

    doc.save(`storico_movimenti_${item.ean || 'report'}.pdf`);
  } catch (err) {
    console.error("PDF Error:", err);
    alert("Errore durante la generazione del PDF");
  }
};


  const handleEditClick = async () => {
    if (isEditing) {
      handleSave();
      return;
    }

    setIsCheckingAuth(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('Autenticazione richiesta');

      const { data: profil, error: dbError } = await supabase
        .from('profili')
        .select('ruolo')
        .eq('id', user.id)
        .single();

      if (dbError || !profil) throw new Error('Profilo non trovato');

      if (profil.ruolo === 'cco-admin' || profil.ruolo === 'Owner') {
        setIsEditing(true);
      } else {
        setNotification({ type: 'error', message: 'Accesso negato: Permessi admin richiesti' });
      }
    } catch (err) {
      setNotification({ type: 'error', message: err.message });
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('cco_storico')
        .update({
          data: formData.data,
          nota: formData.nota
        })
        .eq('id', item.id);

      if (error) throw error;
      
      setNotification({ type: 'success', message: 'Record aggiornato!' });
      if (onUpdate) onUpdate(formData);
      setIsEditing(false);
    } catch (err) {
      setNotification({ type: 'error', message: 'Errore: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Rimuovere permanentemente questo record?")) return;
    setLoading(true);
    try {
      const { error } = await supabase.from('cco_storico').delete().eq('id', item.id);
      if (error) throw error;
      setNotification({ type: 'success', message: 'Record rimosso' });
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setNotification({ type: 'error', message: err.message });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'data') {
        const isoValue = new Date(value).toISOString();
        setFormData(prev => ({ ...prev, data: isoValue }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const mov = getMovimentoConfig(formData.tipomovimento, formData.qtamovimentata);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 overflow-hidden">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-md animate-in fade-in duration-500" onClick={onClose} />

      {notification && (
        <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[110] px-6 py-4 rounded-[1.5rem] shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-10 duration-500 ${
          notification.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <Icon name={notification.type === 'success' ? 'Check' : 'AlertTriangle'} size={20} />
          <span className="text-sm font-black uppercase tracking-widest">{notification.message}</span>
        </div>
      )}

      <div className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl bg-card border-t md:border border-border/60 rounded-t-[3rem] md:rounded-[3rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.4)] flex flex-col animate-in slide-in-from-bottom-20 duration-500">
        
        {/* Header */}
        <div className={`shrink-0 px-8 pt-8 pb-6 border-b border-border/40 bg-gradient-to-b ${mov.bgHeader}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-3 min-w-0 flex-1">
              <div className="flex items-center gap-3">
                 <div className={`w-3 h-3 rounded-full ${mov.dot}`} />
                 <div className={`px-3 py-1 rounded-full border font-black text-[10px] tracking-[.2em] uppercase ${mov.badge}`}>
                    {formData.tipomovimento}
                 </div>
              </div>
              
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Data e Ora</span>
                {isEditing ? (
                  <input 
                    type="datetime-local"
                    name="data"
                    value={toInputDateTime(formData.data)}
                    onChange={handleInputChange}
                    className="bg-background/50 border border-primary/30 rounded-lg px-2 py-1 text-sm font-bold text-foreground outline-none focus:ring-2 focus:ring-primary/20"
                  />
                ) : (
                  <span className="text-sm font-bold text-foreground/60">{formatFullDate(formData.data)}</span>
                )}
              </div>
            </div>

            <div className="flex gap-2">
                {/* PDF DOWNLOAD BUTTON */}
                <button
                  onClick={downloadPDF}
                  className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-sm border bg-muted/50 text-muted-foreground hover:bg-blue-500/10 hover:text-blue-500 border-border/40"
                  title="Download PDF"
                >
                  <Icon name="FileDown" size={22} />
                </button>

                <button
                  onClick={handleEditClick}
                  disabled={loading || isCheckingAuth}
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-sm border ${
                      isEditing ? 'bg-emerald-500 text-white border-emerald-400' : 'bg-muted/50 text-muted-foreground hover:bg-background border-border/40'
                  }`}
                >
                  <Icon 
                    name={(loading || isCheckingAuth) ? "Loader2" : isEditing ? "Check" : "Settings2"} 
                    size={22} 
                    className={(loading || isCheckingAuth) ? "animate-spin" : ""} 
                  />
                </button>
                <button onClick={onClose} className="w-12 h-12 rounded-2xl bg-muted/50 hover:bg-red-500/10 border border-border/40 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all">
                  <Icon name="X" size={22} />
                </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 scrollbar-hide">
          <div className="grid grid-cols-2 gap-4">
             <div className="bg-muted/30 border border-border/40 p-6 rounded-[2.5rem] flex flex-col items-center justify-center text-center shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2">Q.Tà Movimentata</span>
                <span className={`text-4xl font-black tabular-nums ${mov.color}`}>
                   {mov.symbol}{formData.qtamovimentata}
                </span>
             </div>
             <div className="bg-muted/30 border border-border/40 p-6 rounded-[2.5rem] flex flex-col items-center justify-center text-center shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2">Q.Tà attuale</span>
                <span className="text-4xl font-black tabular-nums text-foreground/80">
                   {formData.qtapresente}
                </span>
             </div>
          </div>

          <Sezione titolo="Core Data" icon="Package" colorClass="text-blue-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Campo label="Denominazione" value={formData.nome} isEditing={isEditing} name="nome" disabled={true} />
              <Campo label="Barcode / EAN" value={formData.ean} mono isEditing={isEditing} name="ean" disabled={true} />
            </div>
          </Sezione>

          <Sezione titolo="Audit & Log" icon="ShieldCheck" colorClass="text-emerald-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Campo label="Operatore" value={formData.utente} isEditing={isEditing} name="utente" disabled={true} />
              <Campo label="Etichetta" value={formData.tipomovimento} isEditing={isEditing} name="tipomovimento" disabled={true} />
            </div>
          </Sezione>

          <Sezione titolo="Note" icon="FileText" colorClass="text-amber-500">
             {isEditing ? (
                <textarea 
                  name="nota" 
                  value={formData.nota ?? ''} 
                  onChange={handleInputChange} 
                  className="w-full bg-background border border-border/60 rounded-[1.5rem] p-4 text-sm font-bold outline-none focus:ring-2 focus:ring-primary/20 min-h-[120px] shadow-inner transition-all"
                />
             ) : (
                <div className="p-4 rounded-2xl bg-background/50 border border-border/20 shadow-sm">
                  <p className="text-sm font-bold text-foreground/70 leading-relaxed italic">
                    {formData.nota ? `"${formData.nota}"` : "Nessuna nota."}
                  </p>
                </div>
             )}
          </Sezione>
        </div>

        <div className="shrink-0 px-8 py-6 border-t border-border/40 bg-muted/10 flex items-center justify-between">
          <div>
            {isEditing && (
              <button onClick={handleDelete} disabled={loading} className="group flex items-center gap-2 px-5 py-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all border border-red-500/20">
                <Icon name="Trash2" size={14} />
                Elimina
              </button>
            )}
          </div>
          <div className="flex gap-3">
             {isEditing ? (
               <>
                 <button onClick={() => { setIsEditing(false); setFormData({...item}); }} className="px-6 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-widest border border-border/40">
                    Annulla
                 </button>
                 <button onClick={handleSave} disabled={loading} className="px-10 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all">
                    {loading ? 'Salvataggio...' : 'Salva'}
                 </button>
               </>
             ) : (
               <button onClick={onClose} className="px-10 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:bg-muted/80 transition-all border border-border/40">
                 Chiudi
               </button>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DettaglioModalStorico;